import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import useStyles from './AdminMessagesDeleteButton.styles';
import { ApolloCache, useMutation } from '@apollo/client';
import { useSelectedLinkContext } from 'contexts/SelectedLinkContext';
import { trackEvent, GAAdminMessagesEventActions, GAEventCategory } from 'services/googleAnalytics';
import {
  DELETE_MESSAGES_MUTATION,
  DeleteMessagesOutput,
  GET_FILTERED_MESSAGES_QUERY,
  GET_RECEIVED_MESSAGES_QUERY,
  GET_SENT_MESSAGES_QUERY,
  GetFilteredMessagesOutput,
  GetReceivedMessagesOutput,
  GetSentMessagesOutput
} from 'graphql/admin/adminmessage';
import { Message } from 'types';
import { navigateToSelectedChannelRoute } from 'utils';
import Route from 'routes/Routes';
import { adminMessagesContext } from '../AdminMessagesContext';
import AdminMessagesMobileDeleteButton from './AdminMessagesMobileDeleteButton';
import {useSnackbar, useSmallDesktopScreen} from 'hooks';
import { FilledButton } from 'components';

const filterDeletedMessages = (
  currentMessages: Message[],
  deletedMessages: string[],
) => currentMessages.filter((_) => !deletedMessages.includes(_.key));

const updateFilteredData = (
  cache: ApolloCache<any>,
  channel: string | undefined,
  deletedMessages: string[],
) => {
  const variables = {
    getFilteredMessagesInput: {
      channel,
    },
  };

  const getMessagesData = cache.readQuery<GetFilteredMessagesOutput>({
    query: GET_FILTERED_MESSAGES_QUERY,
    variables,
  });

  const currentMessages = getMessagesData?.getFilteredMessages?.messages || [];
  const messages = filterDeletedMessages(currentMessages, deletedMessages);

  const updatedData = {
    ...getMessagesData,
    getFilteredMessages: {
      messages,
    },
  };

  cache.writeQuery({
    data: updatedData,
    query: GET_FILTERED_MESSAGES_QUERY,
    variables,
  });
};

const updateReceivedData = (
  cache: ApolloCache<any>,
  channel: string | undefined,
  deletedMessages: string[],
) => {
  const variables = {
    getReceivedMessagesInput: {
      channel,
    },
  };

  const getMessagesData = cache.readQuery<GetReceivedMessagesOutput>({
    query: GET_RECEIVED_MESSAGES_QUERY,
    variables,
  });

  const currentMessages = getMessagesData?.getReceivedMessages?.messages || [];
  const messages = filterDeletedMessages(currentMessages, deletedMessages);

  const updatedData = {
    ...getMessagesData,
    getReceivedMessages: {
      messages,
    },
  };

  cache.writeQuery({
    data: updatedData,
    query: GET_RECEIVED_MESSAGES_QUERY,
    variables,
  });
};

const updateSentData = (
  cache: ApolloCache<any>,
  channel: string | undefined,
  deletedMessages: string[],
) => {
  const variables = {
    getSentMessagesInput: {
      channel,
    },
  };

  const getMessagesData = cache.readQuery<GetSentMessagesOutput>({
    query: GET_SENT_MESSAGES_QUERY,
    variables,
  });

  const currentMessages = getMessagesData?.getSentMessages?.messages || [];
  const messages = filterDeletedMessages(currentMessages, deletedMessages);

  const updatedData = {
    ...getMessagesData,
    getSentMessages: {
      messages,
    },
  };

  cache.writeQuery({
    data: updatedData,
    query: GET_SENT_MESSAGES_QUERY,
    variables,
  });
};

type AdminMessagesDeleteButtonPureProps = {
  disabled: boolean;
  handleDeleteMessages: () => void;
};

const AdminMessagesDeleteButtonPure = ({
  disabled,
  handleDeleteMessages,
}: AdminMessagesDeleteButtonPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <FilledButton
      className={classes.deleteButton}
      disabled={disabled}
      color='red'
      onClick={() => handleDeleteMessages()}
    >
      <FormattedMessage id="button.delete" />
    </FilledButton>
  );
};

const getDeletionResultMessages = (isUniqueDeletion: boolean) => {
  if (isUniqueDeletion) {
    return {
      error: 'admin.messages.delete.unique.error',
      partialSuccess: 'admin.messages.delete.multiple.partialSuccess',
      success: 'admin.messages.delete.unique.success',
    };
  } else {
    return {
      error: 'admin.messages.delete.multiple.error',
      partialSuccess: 'admin.messages.delete.multiple.partialSuccess',
      success: 'admin.messages.delete.multiple.success',
    };
  }
};

type AdminMessagesDeleteButtonProps = {
  isUniqueDeletion?: boolean;
};

const AdminMessagesDeleteButton = ({ isUniqueDeletion,
}: AdminMessagesDeleteButtonProps): JSX.Element => {
  const { selectedLink } = useSelectedLinkContext();
  const { errorSnackbar, successSnackbar, warningSnackbar } = useSnackbar();
  const { selectedMessages, updateSelectedMessages, resetSelectedMessages } = useContext(adminMessagesContext);
  const isSmallScreen = useSmallDesktopScreen();

  const channel = selectedLink?.channelKey;

  const [deleteMessages, { loading }] = useMutation<DeleteMessagesOutput>(
    DELETE_MESSAGES_MUTATION,
    {
      update(cache, { data }) {
        const deletedMessages = data.deleteMessages.successes;

        if (deletedMessages.length > 0) {
          updateFilteredData(cache, channel, deletedMessages);
          updateReceivedData(cache, channel, deletedMessages);
          updateSentData(cache, channel, deletedMessages);
        }

        if (isUniqueDeletion) {
          navigateToSelectedChannelRoute(selectedLink, Route.ADMIN_MESSAGES);
        }
      },
    },
  );

  const deletionResultMessages = getDeletionResultMessages(isUniqueDeletion);

  const handleDeleteMessages = useCallback(() => {
    deleteMessages({
      variables: {
        deleteMessagesInput: {
          channel,
          messageKeys: selectedMessages.map((_) => _.key),
        },
      },
    })
      .then(({ data }) => {
        if (
          data.deleteMessages?.errorReason ||
          data.deleteMessages?.successes?.length === 0
        ) {
          errorSnackbar(
            <FormattedMessage id={deletionResultMessages.error} />,
          );
        } else if (data.deleteMessages?.failures?.length > 0) {
          data.deleteMessages.failures.forEach((_) => {
            const message = selectedMessages.find((m) => m.key === _.subject);

            if (message) {
              updateSelectedMessages(message, false);
            }
          });

          warningSnackbar(
            <FormattedMessage id={deletionResultMessages.partialSuccess} />,
          );
        } else {
          resetSelectedMessages();

          successSnackbar(
            <FormattedMessage id={deletionResultMessages.success} />,
          );

          trackEvent(
            GAEventCategory.ADMIN_MESSAGES,
            GAAdminMessagesEventActions.DELETE_MESSAGES,
            'Messages',
          );
        }
      })
      .catch((error) => {
        console.error(error);
        errorSnackbar(
          <FormattedMessage id={deletionResultMessages.error} />,
        );
      });
  }, [
    channel,
    deletionResultMessages,
    selectedMessages,
    deleteMessages,
    resetSelectedMessages,
    errorSnackbar,
    successSnackbar,
    warningSnackbar,
    updateSelectedMessages,
  ]);

  const disabled = loading || selectedMessages.length === 0;

  if (isSmallScreen) {
    return (
      <AdminMessagesMobileDeleteButton
        disabled={disabled}
        handleDeleteMessages={handleDeleteMessages}
      />
    );
  }

  return (
    <AdminMessagesDeleteButtonPure
      disabled={disabled}
      handleDeleteMessages={handleDeleteMessages}
    />
  );
};

export default AdminMessagesDeleteButton;
